import * as XLSX from 'xlsx';
import { DatabaseMaterial } from '@/hooks/useMaterials';

/**
 * Mappa categoria → sfrido % default (UNI). Mirror di `DEFAULT_WASTE_PCT`
 * in `useWasteFactors.ts`. Usata come ULTIMO fallback se non viene passata
 * la `wasteMap` org-specifica.
 */
const DEFAULT_WASTE_PCT: Record<string, number> = {
  board: 3,
  structure_frame: 2,
  structure_guide: 2,
  insulation: 7,
  screw: 5,
  accessory: 5,
  finish: 10,
  ceiling_tile: 3,
  other: 0,
};

function boolToYesNo(value: boolean | null | undefined) {
  return value === true ? "Sì" : value === false ? "No" : "";
}
function arrToList(value: string[] | null | undefined) {
  return Array.isArray(value) ? value.join(", ") : "";
}

// View `materials_with_pricing` aggiunge i campi derivati (read-only) usati a UI.
type MaterialWithPricing = DatabaseMaterial & {
  family_code?: string | null;
  family_discount_pct?: number | null;
  extra_discount_pct?: number | null;
  total_discount_pct?: number | null;
  net_price?: number | null;
  extra_discount?: number | string | null;
  list_price?: number | null;
  ean_code?: string | null;
  delivery_indicator?: string | null;
  waste_percentage?: number | null;
  disposal_percentage?: number | null;
  is_variable_thickness?: boolean | null;
  mechanical_performance?: string | null;
  thermal_performance_notes?: string | null;
  sustainability_notes?: string | null;
  system_compatibility?: string | null;
  fire_performance_notes?: string | null;
  carbon_footprint?: string | null;
  epd?: string | null;
  vapor_permeability?: string | null;
  thermal_capacity?: string | null;
  box_pieces?: number | null;
  compatible_board_types?: string[] | null;
};

/**
 * Esporta i materiali in Excel.
 *
 * @param materials lista materiali (di solito da `useMaterials`)
 * @param wasteMap  opzionale: map categoria → % sfrido configurata in Settings.
 *                  Se non fornita, fallback ai DEFAULT_WASTE_PCT (UNI).
 *                  Usata per popolare il valore effettivo dello sfrido nella
 *                  colonna "Sfrido %" anche per i materiali senza override.
 */
export function exportMaterialsToExcel(
  materials: DatabaseMaterial[],
  wasteMap?: Record<string, number>,
) {
  // Resolver sfrido effettivo: override del materiale OPPURE default categoria.
  const resolveWastePct = (m: MaterialWithPricing): { value: number; isOverride: boolean } => {
    const override = m.waste_percentage;
    if (override !== null && override !== undefined && Number.isFinite(Number(override))) {
      return { value: Number(override), isOverride: true };
    }
    const fromMap = wasteMap?.[m.category as string];
    const value = fromMap ?? DEFAULT_WASTE_PCT[m.category as string] ?? 0;
    return { value, isOverride: false };
  };

  const worksheetData = (materials as MaterialWithPricing[]).map((m) => {
    const waste = resolveWastePct(m);
    return ({
    // — Identificativi —
    Codice: m.code,
    Nome: m.name,
    Categoria: m.category,
    Fornitore: m.supplier,
    "Codice famiglia": m.family_code ?? "",
    "EAN": m.ean_code ?? "",
    Descrizione: m.description ?? "",
    "Tipo materiale": m.material_type ?? "",
    "Tipologia lastra": m.board_typology ?? "",
    "Tipo lastra": m.board_type ?? "",

    // — Dimensioni e peso —
    "Spessore (mm)": m.thickness ?? "",
    "Spessore variabile": boolToYesNo(m.is_variable_thickness),
    "Larghezza (mm)": m.width ?? "",
    "Lunghezza (mm)": m.length ?? "",
    "Peso (kg/m²)": m.weight_per_sqm ?? "",
    "Peso per ml (kg)": m.weight_per_ml ?? "",
    "Spessore lamiera (mm)": m.sheet_thickness ?? "",
    "Densità (kg/m³)": m.density ?? "",

    // — Prezzo & sconti (source of truth) —
    "Prezzo listino (€)": m.list_price ?? "",
    "Prezzo unitario (€)": m.unit_price ?? "",
    "Unità": m.unit,
    "Sconto (%)": m.discount ?? "",
    "Extra sconto (%)": m.extra_discount ?? "",
    "Indicatore consegna": m.delivery_indicator ?? "",
    "Valido fino al": m.valid_until ?? "",

    // — Prezzi & sconti DERIVATI (read-only, info per analisi — IGNORATI in import) —
    "[D] Sconto famiglia %": m.family_discount_pct ?? "",
    "[D] Sconto extra %": m.extra_discount_pct ?? "",
    "[D] Sconto totale %": m.total_discount_pct ?? "",
    "[D] Prezzo netto (€)": m.net_price ?? "",

    // — PERSONALIZZAZIONE della tua azienda (modificabili e reimportabili) —
    // Valgono solo per la tua org e vincono sul prezzo/tempo di catalogo.
    // Lascia la cella vuota per usare il valore di catalogo.
    "Il tuo prezzo (€)": m.custom_net_price ?? "",
    "Il tuo tempo (min)":
      m.custom_installation_time_per_sqm != null
        ? Math.round(m.custom_installation_time_per_sqm * 60 * 1e4) / 1e4
        : "",

    // — Incidenza & posa —
    "Incidenza Base": m.incidence_base ?? "",
    "Incidenza (unit/m²)": m.incidence_per_sqm ?? "",
    "Passo (mm)": m.passo ?? "",
    "Tempo installazione (ore/m²)": m.installation_time_per_sqm ?? "",
    "Pezzi per scatola": m.box_pieces ?? "",
    // F21.4: sfrido EFFETTIVO (sempre numerico) + colonna 'Sfrido tipo' che
    // chiarisce se è Override esplicito o Default categoria.
    // Beneficio: l'utente vede sempre un numero (no più celle vuote), e in
    // import sappiamo se mantenere NULL (default) o forzare override.
    "Sfrido %": Number(waste.value.toFixed(2)),
    "Sfrido tipo": waste.isOverride ? "Override" : "Default categoria",
    "Smaltimento %": m.disposal_percentage ?? "",
    "Note installazione": m.installation_notes ?? "",

    // — Tecnici —
    "Resistenza a flessione": m.flexural_strength ?? "",
    "Durezza superficiale": m.surface_hardness ?? "",
    "EN 520": m.en_520_type ?? "",
    "Assorbimento acqua": m.water_absorption ?? "",
    "Classe resistenza umidità": m.humidity_resistance_class ?? "",
    "Conduttività termica (λ)": m.thermal_conductivity ?? "",
    "Prestazione acustica (Rw)": m.acoustic_performance ?? "",

    // — Fuoco —
    "Resistenza fuoco - classe": m.fire_resistance_class ?? "",
    "Classe fuoco": m.fire_class ?? "",
    "Descrizione fuoco": m.fire_description ?? "",
    "Note utilizzo fuoco": m.fire_usage_notes ?? "",
    "Note prestazioni fuoco": m.fire_performance_notes ?? "",
    "Compatibile REI": boolToYesNo(m.rei_compatible),

    // — Profili —
    "Tipo profilo": m.profile_type ?? "",
    "Finitura superficiale": m.surface_finish ?? "",

    // — Ambiente / sostenibilità —
    "Certificazione ambientale": m.environmental_certification ?? "",
    "Contenuto riciclato (%)": m.recycled_content ?? "",
    "Classe VOC": m.voc_class ?? "",
    "Note sostenibilità": m.sustainability_notes ?? "",
    "Carbon Footprint": m.carbon_footprint ?? "",
    "EPD": m.epd ?? "",
    "Permeabilità vapore": m.vapor_permeability ?? "",
    "Capacità termica": m.thermal_capacity ?? "",

    // — Performance & sistema —
    "Prestazioni meccaniche": m.mechanical_performance ?? "",
    "Note termo-igrometriche": m.thermal_performance_notes ?? "",
    "Compatibilità sistema": m.system_compatibility ?? "",

    // — Visualizzazione & destinazione —
    "Colore (hex)": m.color_hex ?? "",
    "Destinazioni d'uso": arrToList(m.intended_use as string[] | null | undefined),
    "Lastre compatibili": arrToList(m.compatible_board_types),

    // — Audit (read-only) —
    "[D] Data creazione": m.created_at ?? "",
    "[D] Data ultima modifica": m.updated_at ?? "",
    });
  });

  const worksheet = XLSX.utils.json_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Materiali completi');

  // Foglio leggimi inline per spiegare i campi [D] = derivati (read-only)
  const readme = XLSX.utils.aoa_to_sheet([
    ["Foglio Leggimi"],
    [],
    ["Colonne [D] (derivati / audit)", "Sono prodotti automaticamente — IGNORATI in fase di import."],
    ["Match in fase di import", "La chiave è 'Codice'. Se esiste → UPDATE, se non esiste → INSERT."],
    ["Il tuo prezzo (€)", "Prezzo personalizzato della TUA azienda: vince sul listino e sullo sconto famiglia, e viene usato in tutti i preventivi (comprese le pareti certificate). Cella vuota = usa il prezzo di catalogo. Si ricarica con Importa/Esporta → 'Importa i tuoi prezzi/tempi'."],
    ["Il tuo tempo (min)", "Tempo di posa personalizzato in MINUTI per unità. Cella vuota = usa il tempo di catalogo. Si ricarica con lo stesso wizard."],
    ["Materiali di catalogo", "Nome, dati tecnici e prezzo di listino sono condivisi con tutte le aziende e NON si modificano da qui: per farli tuoi usa 'Il tuo prezzo'/'Il tuo tempo', oppure 'Personalizza completamente' nella tabella materiali."],
    ["Famiglie", "Compila 'Codice famiglia' insieme a 'Fornitore'. In import il sistema risolve l'ID famiglia per quel fornitore. Se la coppia non esiste, family_id rimane nullo."],
    ["Sconti", "'Sconto (%)' e 'Extra sconto (%)' sono i sorgenti (catena cumulativa). I [D] Sconto famiglia/extra/totale sono derivati."],
    ["Sfrido %", "Mostra SEMPRE il valore effettivo applicato (override del materiale OPPURE default categoria)."],
    ["Sfrido tipo", "'Override' = valore custom su questo materiale, scritto in DB. 'Default categoria' = usa il default da Settings → Sfridi (non scrive su DB). In import: se 'Default categoria', il sistema mantiene NULL anche se cambi il numero in 'Sfrido %'. Per forzare un override scrivi 'Override' nella colonna."],
    ["Boolean", "Usare 'Sì' / 'No' (la cella vuota = non modificare)."],
    ["Array", "Liste come 'Lastre compatibili' usano virgola: \"A, B, C\""],
  ]);
  XLSX.utils.book_append_sheet(workbook, readme, 'Leggimi');

  const ts = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(workbook, `listino_materiali_${ts}.xlsx`);
}
